import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import MarketingLayout from './components/MarketingLayout'

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
}
const stagger = { hidden: {}, visible: { transition: { staggerChildren: 0.08 } } }

const effectiveDate = 'August 18, 2026'

const sections = [
  {
    id: 'acceptance',
    title: '1. Acceptance of Terms',
    content: `**EMERGENCY WARNING:** SOS SMS is not an emergency service. For any life-threatening emergency, gas leak, or fire, call 911.

By accessing or using the SOS SMS platform - including our website, SMS dispatch system, mobile interface, and any associated services - you agree to be bound by these Terms and Conditions and our Privacy Policy.

If you do not agree to these terms, you may not use SOS SMS. These terms apply to all users of the platform, including customers (homeowners), service providers, and administrators.

SOS SMS reserves the right to update these Terms at any time. Continued use of the platform following an update constitutes acceptance of the revised Terms.`,
  },
  {
    id: 'description',
    title: '2. Description of Services',
    content: `SOS SMS is an SMS-based emergency home services dispatch platform that:

• Connects homeowners ("Customers") with licensed home service professionals ("Providers") for plumbing, electrical, and HVAC services.
• Facilitates real-time dispatch via SMS through our Twilio-powered messaging system.
• Processes payments from Customers via Stripe and distributes payouts to Providers via Stripe Connect.
• Provides an administrative dashboard for platform management.
• Provides dedicated portals for Providers and Customers to manage their accounts and service history.

SOS SMS acts as a technology intermediary only. We do not employ service professionals and are not responsible for the quality, safety, legality, or timeliness of services performed by Providers.`,
  },
  {
    id: 'eligibility',
    title: '3. Eligibility & Account Requirements',
    content: `**Age Requirement:** You must be at least 18 years old to use SOS SMS.

**Account Accuracy:** You agree to provide accurate, current, and complete information during registration and to keep your account information updated.

**Account Security:** You are responsible for maintaining the confidentiality of your login credentials. You agree to notify us immediately of any unauthorized use of your account.

**One Account Per User:** You may not create multiple accounts to circumvent suspensions, bans, or platform restrictions.

**Provider Requirements:** Service providers must:
• Be a licensed professional in the relevant trade (Plumbing, Electrical, or HVAC)
• Maintain valid liability insurance
• Submit authentic license and insurance documents for verification
• Complete Stripe Connect onboarding for payment processing
• Comply with all applicable local, state, and federal regulations governing their trade`,
  },
  {
    id: 'customer-responsibilities',
    title: '4. Customer Responsibilities',
    content: `As a Customer using SOS SMS, you agree to:

• Provide accurate information about your service needs, address, and contact details.
• Ensure safe access to your property for assigned service providers.
• Not submit false, misleading, or exaggerated service requests.
• Pay the authorized trip charge ($150) as part of the service request process.
• Treat service providers with respect and professionalism.
• Provide honest ratings and reviews based on your actual experience.
• Not share your account credentials with others.
• Communicate directly with Providers regarding the scope, price, parts, labor, and other terms of work beyond the SOS SMS trip charge.
• Not use the platform for fraud, abuse, or misleading service requests.

Customers who abuse the platform, submit fraudulent requests, or fail to honor payment obligations may have their accounts suspended or terminated.`,
  },
  {
    id: 'provider-responsibilities',
    title: '5. Provider Responsibilities',
    content: `As a Service Provider on SOS SMS, you agree to:

• Maintain valid, current licenses and insurance at all times. Immediate notification is required if any credential expires or is revoked.
• Accurately represent your skills, certifications, service areas, and availability.
• Respond promptly to job notifications and honor your accepted ETAs.
• Perform services professionally, safely, and in accordance with applicable trade standards and local codes.
• Treat all customers with courtesy and respect.
• Communicate directly with Customers regarding the scope, price, parts, labor, and other terms of work beyond the SOS SMS trip charge.
• Bill Customers directly for work performed beyond the SOS SMS trip charge.
• Keep your Stripe Connect account active and in good standing for payment processing.
• Not submit false or inflated reviews, ratings, or job completion reports.
• Maintain Google Business Profile information accurately if connected to the platform.

Providers found to be falsifying credentials, performing substandard work, or violating any of these responsibilities may be suspended or permanently removed from the platform.

SOS SMS does not charge Providers lead fees, subscriptions, or a percentage of the Customer's additional work.`,
  },
  {
    id: 'payments',
    title: '6. Payment Terms',
    content: `**SOS SMS Trip Charge:** A flat trip charge of $150 USD is required for each service dispatch. This amount is authorized on your payment method when a provider is assigned and captured upon job completion.

**Authorization & Capture:** SOS SMS uses Stripe for payment processing. When a provider is confirmed, we authorize (but do not immediately charge) $150 on your payment method. This amount is captured when the job is completed.

**Cancellation:** If a job is cancelled before provider arrival, the authorization will be released. Cancellations after provider arrival may result in a partial or full charge at SOS SMS's discretion.

**Provider Payouts:** Providers receive payment via Stripe Connect after job completion. Payout amounts and timing are subject to Stripe's disbursement schedule and our current payout policies.

**Additional Work:** Work performed by Providers beyond the SOS SMS trip charge is agreed and billed directly between the Customer and Provider. SOS SMS does not charge Providers a percentage or fee for this additional work.

**Membership Billing:** Membership costs ($14/month or $99/year) are billed according to the selected plan. Customers can manage or cancel their membership at any time through their account settings or by contacting support@sossms.com.

**Disputes:** Payment disputes must be submitted to SOS SMS via email at support@sossms.com within 7 days of the service date.

**Taxes:** Customers and Providers are responsible for any applicable taxes related to services rendered. SOS SMS provides transaction records but does not provide tax advice.`,
  },
  {
    id: 'membership-terms',
    title: '7. Membership Terms',
    content: `**Membership Plans:** SOS SMS offers Membership at two billing frequencies:
• $14/month (monthly billing)
• $99/year (annual billing)

**Automatic Renewal:** Membership automatically renews at the end of each billing period (monthly or yearly) unless cancelled.

**Cancellation:** You may cancel your Membership at any time through your account settings or by contacting support@sossms.com. Cancellation takes effect at the end of your current billing period and prevents future renewal charges.

**What Membership Includes:** Membership provides access to the SOS SMS platform and the response and network guarantees described in the applicable Guarantee and Refund Policy. Membership does not include the cost of work performed by Providers.

**Trip Charge:** The $150 trip charge applies each time SOS SMS dispatches a Provider to your location. This charge is separate from Membership.

**Additional Work:** Work performed by Providers beyond the SOS SMS trip charge is billed directly between the Customer and Provider. Customers negotiate price, scope, parts, labor, and timeline directly with the assigned Provider. SOS SMS does not charge Providers lead fees, subscriptions, or a percentage of this additional work.`,
  },
  {
    id: 'guarantee-policy',
    title: '8. Response Guarantee & Refund Policy',
    content: `SOS SMS advertises a response guarantee to Customers regarding the speed of provider assignment and dispatch. The details of this guarantee - including coverage areas, service hours, eligible service types, response time, how to claim a refund, excluded circumstances, and refund amount - are subject to the applicable Guarantee and Refund Policy.

Any response-time guarantee offered by SOS SMS is subject to eligibility requirements, covered metros, service hours, exclusions (including weather emergencies and force majeure events), and the refund process described in the applicable guarantee documentation.

For the most current and detailed Guarantee and Refund Policy, please contact support@sossms.com or check your account dashboard.`,
  },
  {
    id: 'platform-limitations',
    title: '9. Platform Limitations & Disclaimers',
    content: `**Intermediary Only:** SOS SMS is a technology platform that facilitates connections between customers and providers. We do not directly provide plumbing, electrical, or HVAC services.

**Response Guarantee:** Any response-time guarantee advertised by SOS SMS is subject to the eligibility requirements, covered areas, service hours, exclusions, and refund procedures described in the applicable Guarantee and Refund Policy.

**Provider Independence:** Service providers are independent professionals, not employees or agents of SOS SMS. SOS SMS is not liable for the acts, omissions, or conduct of providers.

**Service Quality:** While we verify provider licenses and insurance, SOS SMS does not guarantee the quality, safety, or results of any service performed.

**Platform Availability:** SOS SMS strives for 24/7 uptime but does not guarantee uninterrupted availability. Scheduled maintenance, technical issues, or force majeure events may cause downtime.

**SMS Reliability:** SMS delivery depends on telecommunications carriers and is not guaranteed. SOS SMS is not responsible for delayed or undelivered messages caused by carrier issues.`,
  },
  {
    id: 'intellectual-property',
    title: '10. Intellectual Property',
    content: `All content, trademarks, logos, software, interfaces, and technology associated with SOS SMS are the exclusive property of SOS SMS or its licensors.

You may not:
• Copy, reproduce, or distribute SOS SMS's platform, content, or branding without written permission.
• Reverse engineer, decompile, or attempt to extract the source code of the SOS SMS platform.
• Use SOS SMS's trademarks or logos without prior written authorization.
• Create derivative works based on SOS SMS's platform or content.

Users retain ownership of content they submit (such as service photos and reviews) but grant SOS SMS a non-exclusive, royalty-free license to use such content for platform operation and improvement.`,
  },
  {
    id: 'prohibited-conduct',
    title: '11. Prohibited Conduct',
    content: `The following activities are strictly prohibited on SOS SMS:

• Using the platform for any illegal purpose or in violation of applicable laws.
• Submitting fraudulent service requests or false emergency claims.
• Harassing, threatening, or abusing other users or SOS SMS staff.
• Attempting to gain unauthorized access to any part of the platform.
• Introducing malware, viruses, or harmful code into the platform.
• Scraping, crawling, or harvesting data from the platform without authorization.
• Creating fake accounts or impersonating other users.
• Posting false, defamatory, or misleading reviews.
• Manipulating the dispatch system or misusing customer or provider information.
• Any activity that damages, disrupts, or degrades the platform's performance.

Violations may result in immediate account suspension, permanent termination, and potential legal action.`,
  },
  {
    id: 'termination',
    title: '12. Account Termination',
    content: `**By You:** You may close your account at any time by contacting support@sossms.com. Upon account closure, your access to the platform will be revoked. Pending payments or active jobs must be resolved prior to closure.

**By SOS SMS:** We reserve the right to suspend or terminate any account at our sole discretion for violations of these Terms, fraudulent activity, abusive behavior, or any reason we deem necessary to protect the integrity of the platform or its users.

**Effect of Termination:** Upon termination, your right to use the platform ceases immediately. Data retention policies described in our Privacy Policy continue to apply after account termination.`,
  },
  {
    id: 'liability',
    title: '13. Limitation of Liability',
    content: `To the maximum extent permitted by applicable law:

• SOS SMS's total liability to any user for any claim arising out of or related to these Terms or the platform shall not exceed the amount paid by you to SOS SMS in the 12 months preceding the claim.

• SOS SMS is not liable for any indirect, incidental, consequential, punitive, or special damages, including but not limited to lost profits, data loss, property damage, or personal injury resulting from services performed by providers.

• SOS SMS does not warrant that the platform will be error-free, uninterrupted, or free of harmful components.

Some jurisdictions do not allow certain limitations of liability, so some of the above may not apply to you.`,
  },
  {
    id: 'governing-law',
    title: '14. Governing Law & Disputes',
    content: `These Terms and Conditions shall be governed by the laws of the United States, without regard to conflict of law principles.

Any disputes arising from these Terms or your use of SOS SMS shall first be attempted to be resolved through good-faith negotiation. If a resolution cannot be reached, disputes shall be submitted to binding arbitration in accordance with the American Arbitration Association's rules.

Class action lawsuits and class-wide arbitration are not permitted under these Terms.

If any provision of these Terms is found to be unenforceable, the remaining provisions shall continue in full force and effect.`,
  },
  {
    id: 'sms-communications',
    title: '15. SMS Communications',
    content: `**Express Consent:** SOS SMS requires express written consent before sending SMS messages to you. Consent is provided by checking the SMS consent box during signup or account management.

**Message Types:** SMS messages from SOS SMS may relate to membership billing, service requests, dispatch updates, provider assignments, ETAs, payment confirmations, ratings requests, and other service-related communications.

**Message Frequency:** Message frequency varies depending on your activity and service requests.

**Carrier Charges:** Message and data rates may apply depending on your mobile carrier. Contact your carrier for information about your plan's SMS rates.

**Opt-Out:** Reply STOP to any SOS SMS message to opt out of receiving further messages from SOS SMS.

**Help:** Reply HELP to any SOS SMS message for assistance or additional information.

**Consent Not Condition of Purchase:** Providing SMS consent is not a condition of purchasing goods or services from SOS SMS.

**Carrier Disclaimer:** Carriers are not liable for delayed or undelivered messages.

For questions about SMS communications or to manage your SMS preferences, contact support@sossms.com.`,
  },
  {
    id: 'contact',
    title: '16. Contact Information',
    content: `If you have questions about these Terms and Conditions, please contact us:

**SOS SMS**
Email: support@sossms.com
Mailing Address: 7707 Stickney, Wauwatosa, WI 53213
Subject Line: Terms & Conditions Inquiry

We are committed to addressing your concerns promptly and transparently.`,
  },
]

function Section({ section }) {
  return (
    <motion.div id={section.id} variants={fadeUp} className="scroll-mt-24">
      <h2 className="text-xl font-extrabold text-gray-900 mb-4">{section.title}</h2>
      <div className="text-gray-600 leading-relaxed text-sm space-y-3">
        {section.content.split('\n\n').map((para, i) => {
          if (para.includes('\n•')) {
            const lines = para.split('\n')
            const intro = lines[0].startsWith('•') ? null : lines[0]
            const bullets = lines.filter((l) => l.startsWith('•'))
            return (
              <div key={i}>
                {intro && (
                  <p
                    className="mb-2"
                    dangerouslySetInnerHTML={{
                      __html: intro.replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-800 font-semibold">$1</strong>'),
                    }}
                  />
                )}
                <ul className="space-y-1.5 ml-4">
                  {bullets.map((b, j) => (
                    <li key={j} className="flex items-start gap-2">
                      <span className="text-primary mt-1 flex-shrink-0">•</span>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: b
                            .replace(/^•\s*/, '')
                            .replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-800">$1</strong>'),
                        }}
                      />
                    </li>
                  ))}
                </ul>
              </div>
            )
          }
          return (
            <p
              key={i}
              dangerouslySetInnerHTML={{
                __html: para.replace(/\*\*(.*?)\*\*/g, '<strong class="text-gray-800 font-semibold">$1</strong>'),
              }}
            />
          )
        })}
      </div>
    </motion.div>
  )
}

function TermsPage() {
  useEffect(() => {
    document.title = 'Terms & Conditions | SOS SMS'
    document.querySelector('meta[name="description"]')?.setAttribute(
      'content',
      "Read the terms governing your use of SOS SMS's emergency dispatch platform, including payments, provider responsibilities, and liability."
    )
  }, [])

  return (
    <MarketingLayout>

      {/* Hero */}
      <section className="bg-gradient-to-br from-green-900 via-green-800 to-emerald-700 text-white py-20 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
            <defs><pattern id="grid6" width="40" height="40" patternUnits="userSpaceOnUse"><path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5" /></pattern></defs>
            <rect width="100%" height="100%" fill="url(#grid6)" />
          </svg>
        </div>
        <div className="relative max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="text-green-300 font-semibold text-sm uppercase tracking-wider mb-3">Legal</motion.p>
            <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl font-extrabold mb-4">Terms &amp; Conditions</motion.h1>
            <motion.p variants={fadeUp} className="text-green-100">Effective Date: {effectiveDate}</motion.p>
          </motion.div>
        </div>
        <div className="absolute bottom-0 left-0 right-0">
          <svg viewBox="0 0 1440 60" className="w-full h-12 fill-white" preserveAspectRatio="none">
            <path d="M0,32 C360,60 1080,0 1440,32 L1440,60 L0,60 Z" />
          </svg>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-12">

            {/* Table of Contents */}
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Contents</h3>
                <nav className="space-y-1">
                  {sections.map((s) => (
                    <a
                      key={s.id}
                      href={`#${s.id}`}
                      className="block text-sm text-gray-500 hover:text-primary py-1 border-l-2 border-transparent hover:border-primary pl-3 transition-colors"
                    >
                      {s.title}
                    </a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main content */}
            <main className="lg:col-span-3">
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5 mb-10 text-sm text-gray-700">
                <strong className="text-gray-900">Please read these Terms carefully.</strong> By using SOS SMS, you agree to be bound by these Terms and Conditions. These terms govern your use of the platform as a customer, service provider, or administrator.
              </div>

              <motion.div
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={stagger}
                className="space-y-12 divide-y divide-gray-100"
              >
                {sections.map((section) => (
                  <div key={section.id} className="pt-10 first:pt-0">
                    <Section section={section} />
                  </div>
                ))}
              </motion.div>

              <div className="mt-12 pt-8 border-t border-gray-100 flex flex-col sm:flex-row items-center justify-between gap-4">
                <p className="text-gray-500 text-sm">
                  Also see our{' '}
                  <Link to="/privacy-policy" className="text-primary hover:underline">Privacy Policy</Link>
                </p>
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-green-700 transition-colors text-sm"
                >
                  Contact Us with Questions
                </Link>
              </div>
            </main>

          </div>
        </div>
      </section>

    </MarketingLayout>
  )
}

export default TermsPage
